// @flow
import React, { useContext } from "react";
import classnames from "classnames";
import { useFormState } from "react-final-form";

import { read, readAndReplace } from "modules/ChangeServiceForm/misc/utilities";
import type { switchProviderOrMoveT } from "../../fields/SwitchProviderOrMove";
import type { propertyT } from "../../fields/PropertyType";
import { Row } from "../../shared/CommonComponents";
import { PROVIDER_IS_NOT_IN_LIST } from "Commons/Modules/Provider/misc/Constants";

import Styles from "./FormOverview.module.scss";

import ChangeServiceFormSharedContext from "../../../misc/context";

type Props = {
  switchProviderOrMoveData: {
    buttons: {
      elements: {
        [key: string]: switchProviderOrMoveT,
      },
    },
  },
  propertyTypeData: {
    buttons: {
      elements: {
        [key: string]: propertyT,
      },
    },
  },
};

const FormOverview = ({ switchProviderOrMoveData, propertyTypeData }: Props) => {
  const {
    category: { categoryType },
    configurationData: {
      translations: { overview, glossary },
    },
  } = useContext(ChangeServiceFormSharedContext);

  const formValues = useFormState().values;

  const providerName = formValues.currentProviderName;
  const tariffName = formValues.currentTariffName;
  const ecoTariff = formValues.ecoEnergy;
  const reasonIcon = switchProviderOrMoveData.buttons.elements[formValues.switchProviderOrMove];
  const propertyIcon = propertyTypeData.buttons.elements[formValues.propertyType];

  return (
    <div className={Styles.formOverview}>
      <Row className={Styles.row}>
        <span>{read(overview.reasonForChange)}</span>
        <span className={classnames(Styles.icon, "theme-color-primary")}>
          <i className={reasonIcon.iconWebReference} />
          <span>{reasonIcon.label}</span>
        </span>
      </Row>
      <Row className={Styles.row}>
        <span>{read(overview.propertyType)}</span>
        <span className={classnames(Styles.icon, "theme-color-primary")}>
          <i className={propertyIcon.iconWebReference} />
          <span>{propertyIcon.label}</span>
        </span>
      </Row>
      <Row className={Styles.row}>
        <span>{read(overview.annualConsumption)}</span>
        <span>{formValues.yearlyConsumption}</span>
      </Row>
      <Row className={Styles.row}>
        <span>{read(overview.ecoTariff)}</span>
        <span>{ecoTariff === "YES_ECO_TARIFF" ? "Ja" : "Nein"}</span>
      </Row>
      {formValues.categoryProviderId !== PROVIDER_IS_NOT_IN_LIST && (
        <>
          <Row className={Styles.row}>
            <span>
              {readAndReplace(overview.previousProvider, {
                replace: (domNode) => {
                  if (domNode.attribs && domNode.attribs.id === "replace") {
                    return (
                      <b className={Styles.catPrefix}>{categoryType && glossary[categoryType.toLowerCase()].trans}</b>
                    );
                  }
                },
              })}
            </span>
            <span>{providerName}</span>
          </Row>
          <Row className={Styles.row}>
            <span>{read(overview.previousTariff)}</span>
            <span>{tariffName}</span>
          </Row>
        </>
      )}
      {formValues.categoryProviderId === PROVIDER_IS_NOT_IN_LIST && formValues.comment && (
        <Row className={Styles.row}>
          <span>
            {readAndReplace(overview.previousProvider, {
              replace: (domNode) => {
                if (domNode.attribs && domNode.attribs.id === "replace") {
                  return (
                    <b className={Styles.catPrefix}>{categoryType && glossary[categoryType.toLowerCase()].trans}</b>
                  );
                }
              },
            })}
          </span>
          <span>{formValues.comment}</span>
        </Row>
      )}
      {formValues.customerNumber && (
        <>
          <Row className={Styles.row}>
            <span>{read(overview.customerNumber)}</span>
            <span>{formValues.customerNumber}</span>
          </Row>
          <Row className={Styles.row}>
            <span>{read(overview.counterNumber)}</span>
            <span>{formValues.counterNumber ? formValues.counterNumber : formValues.futureCounterNumber}</span>
          </Row>
        </>
      )}
      <Row className={Styles.row}>
        <span>{read(overview.deliveryAddress)}</span>
        <span>
          {formValues.firstname} {formValues.lastname}
          <br />
          {formValues.companyName && (
            <>
              {formValues.companyName}
              <br />
            </>
          )}
          {formValues.street} {formValues.streetNumber}
          <br />
          {formValues.postalcode} {formValues.locationMainName}
        </span>
      </Row>
      {formValues.taxId && (
        <Row className={Styles.row}>
          <span>{read(overview.taxId)}</span>
          <span>{formValues.taxId}</span>
        </Row>
      )}
      {formValues.differentFirstname && (
        <Row className={Styles.row}>
          <span>{read(overview.billingAddress)}</span>
          <span>
            {formValues.differentFirstname} {formValues.differentLastname}
            <br />
            {formValues.differentCompanyName && (
              <>
                {formValues.differentCompanyName}
                <br />
              </>
            )}
            {formValues.differentStreet} {formValues.differentStreetNumber}
            <br />
            {formValues.differentPostalcode} {formValues.differentLocationMainName}
          </span>
        </Row>
      )}
      {formValues.differentAccountHolderFirstname && (
        <Row className={Styles.row}>
          <span>{read(overview.accountOwnerAddress)}</span>
          <span>
            {formValues.differentAccountHolderFirstname} {formValues.differentAccountHolderLastname}
            <br />
            {formValues.differentAccountHolderCompanyName && (
              <>
                {formValues.differentAccountHolderCompanyName}
                <br />
              </>
            )}
            {formValues.differentAccountHolderStreet} {formValues.differentAccountHolderStreetNumber}
            <br />
            {formValues.differentAccountHolderPostalcode} {formValues.differentAccountHolderLocationMainName}
          </span>
        </Row>
      )}
      {formValues.differentAccountHolderTaxId && (
        <Row className={Styles.row}>
          <span>{read(overview.taxId)}</span>
          <span>{formValues.differentAccountHolderTaxId}</span>
        </Row>
      )}
      <Row className={Styles.row}>
        {formValues.accountIban ? (
          <>
            <span>{read(overview.iban)}</span>
            <span>{formValues.accountIban}</span>
          </>
        ) : (
          <>
            <span>{read(overview.blz) + " - " + read(overview.accountNumber)}</span>
            <span>
              {formValues.accountBankNumber} - {formValues.accountNumber}
            </span>
          </>
        )}
      </Row>
      <Row className={Styles.row}>
        <span>{read(overview.terminationDate)}</span>
        <span>
          {formValues.cancelOldContract === "NEXT_POSSIBLE_DATE"
            ? read(overview.nextPossibleDate)
            : formValues.desiredProviderChangeDate
            ? formValues.desiredProviderChangeDate
            : formValues.canceledMyselfDate}
        </span>
      </Row>
    </div>
  );
};

export default FormOverview;
